// src/read-write.ts
import fs from "node:fs/promises";

// src/types.ts
import {
  ElementType,
  ComponentType,
  CustomElementScriptType,
  EmbedCategory,
  EmbeddedScriptPages,
  EmbeddedScriptPlacement,
  ExtendingComponentType,
  HeightMode,
  RegionType,
  PanelContentType,
  Width as PanelSizeWidth,
  PanelMode,
  AlgorithmType
} from "@wix/ambassador-devcenter-apps-v1-app-old/types";
import { z } from "zod";
var errorReportingSchema = z.object({
  errorReporting: z.object({
    url: z.string().optional(),
    // optional because of yoshi
    artifact: z.object({
      fullArtifactId: z.string().max(255).min(1),
      version: z.string().max(255).min(1)
    }).optional()
  }).optional()
});
var componentSourceSchema = z.object({
  iframeUrl: z.string().optional(),
  scriptAsset: z.object({
    exportedName: z.string(),
    containerId: z.string(),
    url: z.string(),
    scriptType: z.string().optional()
  }).optional()
});
var backendWorkerSchema = z.object({
  compType: z.literal(ComponentType.BACKEND_WORKER),
  compId: z.string(),
  compName: z.string().optional(),
  compData: z.object({
    backendWorker: z.object({
      deploymentId: z.string().uuid(),
      deploymentUrl: z.string().url().optional(),
      label: z.string().max(200).optional(),
      commitHash: z.string().max(40).optional()
    })
  })
});
var backofficePageCompDataSchema = z.object({
  hostingPlatform: z.string(),
  fullPage: z.boolean().nullish(),
  hideInSidebar: z.boolean().nullish(),
  priority: z.number().nullish(),
  routePath: z.string().nullish(),
  title: z.string(),
  pageCategoryId: z.string().uuid().nullish(),
  activeSidebarPageId: z.string().nullish(),
  requiredPermission: z.string().nullish(),
  previousRoutePaths: z.array(z.string()).nullish()
}).merge(errorReportingSchema).merge(componentSourceSchema).passthrough();
var widgetCompDataSchema = z.object({
  width: z.number().nullish(),
  height: z.number().nullish()
}).merge(componentSourceSchema).passthrough();
var componentExperimentSchema = z.object({
  appId: z.string(),
  componentId: z.string().uuid(),
  spec: z.string().min(1),
  variantValue: z.string()
}).passthrough().optional();
var backofficePageSchema = z.object({
  compId: z.string(),
  compType: z.literal(ComponentType.BACK_OFFICE_PAGE),
  compData: z.object({
    backOfficePage: backofficePageCompDataSchema
  }),
  compName: z.string().optional(),
  experiment: componentExperimentSchema
}).passthrough();
var backofficeExtensionSchema = z.object({
  compId: z.string(),
  compType: z.literal(ComponentType.BACK_OFFICE_EXTENSION),
  compData: z.object({
    backOfficeExtension: z.object({
      hostingPlatform: z.string(),
      extends: z.string(),
      title: z.string().nullish(),
      extensionType: z.literal(
        ExtendingComponentType.BACK_OFFICE_EXTENSION_WIDGET
      ),
      requiredPermission: z.string().nullish(),
      widget: widgetCompDataSchema
    }).merge(errorReportingSchema).passthrough()
  }),
  compName: z.string().optional(),
  experiment: componentExperimentSchema
}).passthrough();
var backofficeExtensionWidgetSchema = z.object({
  compId: z.string(),
  compType: z.literal(ComponentType.BACK_OFFICE_EXTENSION_WIDGET),
  compData: z.object({
    backOfficeExtensionWidget: z.object({
      hostingPlatform: z.string(),
      extends: z.string(),
      title: z.string().nullish(),
      description: z.string().optional(),
      requiredPermission: z.string().nullish(),
      width: z.number().nullish(),
      height: z.number().nullish()
    }).merge(componentSourceSchema).merge(errorReportingSchema).passthrough()
  }),
  compName: z.string().optional(),
  experiment: componentExperimentSchema
}).passthrough();
var backofficeExtensionMenuPluginSchema = z.object({
  compId: z.string(),
  compType: z.literal(ComponentType.BACK_OFFICE_EXTENSION_MENU_ITEM),
  compData: z.object({
    backOfficeExtensionMenuItem: z.object({
      id: z.string().uuid(),
      title: z.string().min(1),
      subtitle: z.string().max(200).optional(),
      iconKey: z.string().min(3).max(50).optional(),
      extends: z.string().uuid(),
      action: z.object({
        navigateToPage: z.object({
          pageId: z.string().uuid(),
          relativeUrl: z.string().optional()
        }),
        openModal: z.object({
          componentId: z.string().uuid(),
          componentParams: z.record(z.string().min(1), z.string().min(1)).optional()
        })
      }).partial().superRefine((data, ctx) => {
        if (!data.navigateToPage && !data.openModal) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "action must have either navigateToPage or openModal"
          });
        }
        if (data.navigateToPage && data.openModal) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "action can't have both navigateToPage and openModal"
          });
        }
      }),
      sentryDSN: z.string().optional(),
      requiredPermission: z.string().optional()
    }).merge(componentSourceSchema).merge(errorReportingSchema).passthrough()
  }),
  compName: z.string().optional(),
  experiment: componentExperimentSchema
}).passthrough();
var backofficeModalSchema = z.object({
  compId: z.string(),
  compType: z.literal(ComponentType.BACK_OFFICE_MODAL),
  compData: z.object({
    backOfficeModal: z.object({
      hostingPlatform: z.string(),
      title: z.string().nullish(),
      requiredPermission: z.string().nullish(),
      width: z.number().nullish(),
      height: z.number().nullish()
    }).merge(componentSourceSchema).merge(errorReportingSchema).passthrough()
  }),
  compName: z.string().optional(),
  experiment: componentExperimentSchema
}).passthrough();
var backofficeSideCategorySchema = z.object({
  compId: z.string(),
  compType: z.literal(ComponentType.BACK_OFFICE_SIDEBAR_CATEGORY),
  compData: z.object({
    backOfficeSidebarCategory: z.object({
      hostingPlatform: z.string(),
      title: z.string(),
      priority: z.number().nullish()
    })
  }),
  compName: z.string().optional()
}).passthrough();
var backofficeWidgetSchema = z.object({
  compId: z.string(),
  compType: z.literal(ComponentType.BACK_OFFICE_WIDGET),
  compName: z.string().optional()
}).passthrough();
var backofficeExtensionContainer = z.object({
  compId: z.string(),
  compType: z.literal(ComponentType.BACK_OFFICE_EXTENSION_CONTAINER),
  compName: z.string().optional()
}).passthrough();
var wixDependencySchema = z.object({
  packageName: z.string().min(2).max(100),
  version: z.number().int().min(0)
});
var embeddedScriptComponentData = z.object({
  template: z.string(),
  name: z.string(),
  pages: z.enum([
    EmbeddedScriptPages.NONE_PAGES,
    EmbeddedScriptPages.ONCE,
    EmbeddedScriptPages.SPECIFIC_PAGES
  ]),
  placement: z.enum([
    EmbeddedScriptPlacement.HEAD,
    EmbeddedScriptPlacement.BODY_START,
    EmbeddedScriptPlacement.BODY_END,
    EmbeddedScriptPlacement.NONE_PLACEMENT
  ]),
  connectArticleUrl: z.string(),
  embedCategory: z.enum([
    EmbedCategory.UNKNOWN,
    EmbedCategory.FUNCTIONAL,
    EmbedCategory.ESSENTIAL,
    EmbedCategory.ANALYTICS,
    EmbedCategory.ADVERTISING
  ]),
  loadOnce: z.boolean(),
  allowedForFreeSite: z.boolean(),
  dependencies: z.array(wixDependencySchema)
});
var embeddedScriptSchema = z.object({
  compId: z.string(),
  compType: z.literal(ComponentType.EMBEDDED_SCRIPT),
  compName: z.string().optional(),
  compData: z.object({
    embeddedScriptComponentData
  })
}).passthrough();
var customElementPreset = z.object({
  id: z.string(),
  name: z.string().max(36),
  thumbnailUrl: z.string()
});
var customElementWidget = z.object({
  base: z.object({
    id: z.string().optional(),
    name: z.string()
  }),
  installation: z.object({
    base: z.object({
      autoAdd: z.boolean().optional(),
      essential: z.boolean().optional(),
      maxInstances: z.number().optional()
    }).optional(),
    widget: z.object({
      default_preset: z.object({
        desktop_preset_id: z.string().optional(),
        tablet_preset_id: z.string().optional(),
        mobile_preset_id: z.string().optional()
      }).optional(),
      region: z.enum([
        RegionType.BODY,
        RegionType.FOOTER,
        RegionType.HEADER
      ])
    }).optional()
  }).optional(),
  presets: z.array(customElementPreset).optional(),
  widgetData: z.object({
    tagName: z.string(),
    scriptUrl: z.string(),
    scriptType: z.enum([
      CustomElementScriptType.NONE,
      CustomElementScriptType.COMMON_JS,
      CustomElementScriptType.ES_MODULE
    ])
  }),
  behaviors: z.object({
    settings: z.object({
      settingsUrl: z.string()
    }).optional(),
    dashboard: z.object({
      dashboardPageComponentId: z.string()
    }).optional()
  }),
  size: z.object({
    width: z.object({
      allowStretch: z.boolean().optional(),
      defaultWidth: z.number().optional(),
      stretchByDefault: z.boolean().optional()
    }),
    height: z.object({
      defaultHeight: z.number(),
      heightMode: z.enum([HeightMode.AUTO, HeightMode.FIXED, HeightMode.UNKNOWN]).optional()
    })
  }),
  dependencies: z.array(wixDependencySchema)
});
var customElementSchema = z.object({
  compId: z.string(),
  compType: z.literal(ComponentType.CUSTOM_ELEMENT_WIDGET),
  compName: z.string().optional(),
  compData: z.object({
    customElementWidget
  })
}).passthrough();
var widgetPlugin = z.object({
  referenceComponentId: z.string().uuid(),
  marketData: z.object({
    name: z.string(),
    description: z.string().optional(),
    logoUrl: z.string().optional()
  }),
  placements: z.array(
    z.object({
      appDefinitionId: z.string().uuid(),
      widgetId: z.string().uuid(),
      slotId: z.string()
    })
  ),
  installation: z.object({
    base: z.object({
      autoAdd: z.boolean().optional(),
      essential: z.boolean().optional(),
      maxInstances: z.number().optional()
    })
  }).optional()
});
var sitePluginSchema = z.object({
  compId: z.string(),
  compType: z.literal(ComponentType.WIDGET_PLUGIN),
  compName: z.string().optional(),
  compData: z.object({
    widgetPlugin
  })
}).passthrough();
var blocksWidgetComponentData = z.object({
  widgetBundleUrl: z.string()
});
var blocksWidgetSchema = z.object({
  compId: z.string(),
  compType: z.literal(ComponentType.STUDIO_WIDGET),
  compName: z.string().optional(),
  compData: z.object({
    studioWidgetComponentData: z.record(z.string(), z.unknown()).and(blocksWidgetComponentData)
  })
}).passthrough();
var blocksMainComponentSchema = z.object({
  compId: z.string(),
  compType: z.literal(ComponentType.STUDIO),
  compName: z.string().optional(),
  compData: z.object({
    studioComponentData: z.record(z.string(), z.unknown()).optional()
  })
}).passthrough();
var dataComponentSchema = z.object({
  compId: z.string(),
  compType: z.literal(ComponentType.DATA_COMPONENT),
  compName: z.string().optional(),
  compData: z.object({
    dataComponent: z.record(z.string(), z.unknown())
  })
}).passthrough();
var panelContentSchema = z.discriminatedUnion("contentType", [
  z.object({
    contentType: z.literal(PanelContentType.url),
    url: z.string()
  }),
  z.object({
    contentType: z.literal(PanelContentType.code),
    code: z.object({
      bundleUrl: z.string(),
      cssUrl: z.string().optional()
    })
  })
]);
var panelComponentDataSchema = z.object({
  referenceId: z.string().optional(),
  displayName: z.string().optional(),
  size: z.object({
    width: z.enum([
      PanelSizeWidth.SMALL,
      PanelSizeWidth.MEDIUM,
      PanelSizeWidth.LARGE,
      PanelSizeWidth.UNKNOWN_WIDTH
    ]),
    height: z.number().min(150)
  }).optional(),
  panelMode: z.enum([PanelMode.overlay]).optional()
}).and(panelContentSchema);
var panelSchema = z.object({
  compId: z.string(),
  compType: z.literal(ComponentType.PANEL),
  compName: z.string().optional(),
  compData: z.object({
    panel: panelComponentDataSchema
  })
}).passthrough();
var webhookSchema = z.object({
  compId: z.string().uuid(),
  compType: z.literal(ComponentType.WEBHOOK),
  compName: z.string().optional(),
  compData: z.object({
    webhook: z.object({
      webhookSlug: z.string(),
      callbackUrl: z.string()
    })
  })
}).passthrough();
var spiBaseUriSchema = z.object({
  baseUri: z.string(),
  alternativeUris: z.array(
    z.object({
      absoluteUri: z.string(),
      methodName: z.string()
    })
  ).optional()
});
var staffSortingProviderSchema = z.object({
  compId: z.string().uuid(),
  compType: z.literal(ComponentType.STAFF_SORTING_PROVIDER),
  compName: z.string().optional(),
  compData: z.object({
    staffSortingProvider: z.object({
      deploymentUri: spiBaseUriSchema,
      methodName: z.string().max(100),
      methodDescription: z.string().max(100),
      dashboardPluginId: z.string().optional()
    })
  })
});
var bookingsPricingProviderSchema = z.object({
  compId: z.string().uuid(),
  compType: z.literal(ComponentType.BOOKINGS_PRICING_PROVIDER),
  compName: z.string().optional(),
  compData: z.object({
    bookingsPricingProvider: z.object({
      deploymentUri: z.string(),
      pricingProviderName: z.string()
    })
  })
});
var ecomShippingRatesSchema = z.object({
  compId: z.string().uuid(),
  compType: z.literal(ComponentType.ECOM_SHIPPING_RATES),
  compName: z.string().optional(),
  compData: z.object({
    ecomShippingRates: z.object({
      name: z.string(),
      deploymentUri: z.string(),
      description: z.string().optional(),
      learnMoreUrl: z.string().url().optional(),
      dashboardUrl: z.string().url().optional(),
      fallbackDefinitionMandatory: z.boolean().optional(),
      thumbnailUrl: z.string().url().optional()
    })
  })
}).passthrough();
var ecomAdditionalFeesSchema = z.object({
  compId: z.string().uuid(),
  compType: z.literal(ComponentType.ECOM_ADDITIONAL_FEES),
  compName: z.string().optional(),
  compData: z.object({
    ecomAdditionalFees: z.object({
      deploymentUri: z.string()
    })
  })
}).passthrough();
var ecomDiscountsTriggerSchema = z.object({
  compId: z.string().uuid(),
  compType: z.literal(ComponentType.ECOM_DISCOUNTS_TRIGGER),
  compName: z.string().optional(),
  compData: z.object({
    ecomDiscountsTrigger: z.object({
      deploymentUri: z.string()
    })
  })
}).passthrough();
var ecomValidationsSchema = z.object({
  compId: z.string().uuid(),
  compType: z.literal(ComponentType.ECOM_VALIDATIONS),
  compName: z.string().optional(),
  compData: z.object({
    ecomValidations: z.object({
      deploymentUri: z.string(),
      validateInCart: z.boolean().optional()
    })
  })
}).passthrough();
var ecomPaymentSettingsSchema = z.object({
  compId: z.string().uuid(),
  compType: z.literal(ComponentType.ECOM_PAYMENT_SETTINGS),
  compName: z.string().optional(),
  compData: z.object({
    ecomPaymentSettings: z.object({
      deploymentUri: z.string(),
      fallbackValueForRequires3dSecure: z.boolean().optional()
    })
  })
}).passthrough();
var ecomGiftCardsProviderSchema = z.object({
  compId: z.string().uuid(),
  compType: z.literal(ComponentType.GIFT_CARDS_PROVIDER),
  compName: z.string().optional(),
  compData: z.object({
    giftCardsProvider: z.object({
      deploymentUri: z.string(),
      fallbackValueForRequires3dSecure: z.boolean().optional()
    })
  })
}).passthrough();
var recommendationsAlgorithm = z.object({
  algorithmId: z.string().uuid(),
  name: z.string(),
  algorithmType: z.enum([AlgorithmType.GLOBAL, AlgorithmType.RELATED_ITEMS]),
  description: z.string(),
  additionalInfo: z.string().optional()
});
var ecomRecommendationsProviderSchema = z.object({
  compId: z.string().uuid(),
  compType: z.literal(ComponentType.ECOM_RECOMMENDATIONS_PROVIDER),
  compName: z.string().optional(),
  compData: z.object({
    ecomRecommendationsProvider: z.object({
      deploymentUri: z.string(),
      catalogAppIds: z.array(z.string()),
      supportedAlgorithms: z.array(recommendationsAlgorithm)
    })
  })
}).passthrough();
var seoKeywordsSuggestionSchema = z.object({
  compId: z.string().uuid(),
  compType: z.literal(ComponentType.SEO_KEYWORDS_SUGGESTIONS),
  compName: z.string().optional(),
  compData: z.object({
    seoKeywordsSuggestions: z.object({
      baseUri: z.string(),
      upgradeUrl: z.string().optional(),
      landingPageUrl: z.string(),
      quotaEnabled: z.boolean().optional(),
      supportedCountryCodes: z.array(z.string())
    })
  })
}).passthrough();
var premiumCustomChargesSchema = z.object({
  compId: z.string().uuid(),
  compType: z.literal(ComponentType.PREMIUM_CUSTOM_CHARGES),
  compName: z.string().optional(),
  compData: z.object({
    premiumCustomCharges: z.object({
      baseUri: spiBaseUriSchema
    })
  })
}).passthrough();
var externalDatabaseProviderSchema = z.object({
  compId: z.string().uuid(),
  compType: z.literal(ComponentType.EXTERNAL_DATABASE_PROVIDER),
  compName: z.string().optional(),
  compData: z.object({
    externalDatabaseProvider: z.object({
      namespace: z.string(),
      uriConfig: spiBaseUriSchema
    })
  })
}).passthrough();
var topologySchema = z.object({
  compType: z.literal(ComponentType.TOPOLOGY),
  compId: z.string(),
  compName: z.string().optional(),
  compData: z.object({
    topology: z.object({
      topology: z.object({
        backendApiUrl: z.string().url()
      })
    })
  })
}).passthrough();
var monitoringSchema = z.object({
  compType: z.literal(ComponentType.MONITORING),
  compId: z.string(),
  compName: z.string().optional(),
  compData: z.object({
    monitoring: z.unknown()
  })
});
var siteComponentResourcesSchema = z.object({
  client: z.object({
    componentUrl: z.string(),
    cssUrl: z.string().optional()
  }).passthrough(),
  server: z.object({
    url: z.string()
  }).passthrough().optional(),
  sdk: z.object({
    url: z.string()
  }).passthrough().optional(),
  editor: z.object({
    componentUrl: z.string(),
    cssUrl: z.string().optional()
  }).passthrough().optional()
}).passthrough();
var siteComponentPresetItemSchema = z.object({
  cssUrl: z.string(),
  presetCssUrl: z.string(),
  displayName: z.string(),
  thumbnailUrl: z.string().optional()
}).passthrough();
var siteComponentPresetsSchema = z.record(
  z.string(),
  siteComponentPresetItemSchema
);
var siteComponentElementBaseSchema = {
  selector: z.string(),
  displayName: z.string().max(50)
};
var inlineElementSchema = z.lazy(
  () => z.object({
    ...siteComponentElementBaseSchema,
    presets: siteComponentPresetsSchema.optional(),
    elements: z.record(z.string(), siteComponentElementItemSchema).optional()
  }).passthrough()
);
var refElementSchema = z.lazy(
  () => z.object({
    ...siteComponentElementBaseSchema,
    resolvedElement: inlineElementSchema.optional(),
    type: z.string().min(4).max(100)
  }).passthrough()
);
var siteComponentElementItemSchema = z.lazy(
  () => z.discriminatedUnion("elementType", [
    z.object({
      elementType: z.literal(ElementType.inlineElement),
      inlineElement: inlineElementSchema
    }),
    z.object({
      elementType: z.literal(ElementType.refElement),
      refElement: refElementSchema
    })
  ])
);
var siteComponentElementsSchema = z.record(
  z.string(),
  siteComponentElementItemSchema
);
var siteComponentSchema = z.object({
  compType: z.literal(ComponentType.EDITOR_REACT_COMPONENT),
  compId: z.string(),
  compName: z.string().optional(),
  compData: z.object({
    editorReactComponent: z.object({
      type: z.string().min(1),
      description: z.string().min(5),
      resources: siteComponentResourcesSchema,
      installation: z.unknown(),
      editorElement: z.object({
        ...siteComponentElementBaseSchema,
        presets: siteComponentPresetsSchema.optional(),
        elements: siteComponentElementsSchema.optional()
      }).passthrough()
    })
  })
});
var knownComponentSchema = z.discriminatedUnion("compType", [
  backendWorkerSchema,
  backofficePageSchema,
  backofficeExtensionSchema,
  backofficeExtensionWidgetSchema,
  backofficeExtensionMenuPluginSchema,
  backofficeModalSchema,
  backofficeSideCategorySchema,
  backofficeWidgetSchema,
  backofficeExtensionContainer,
  embeddedScriptSchema,
  customElementSchema,
  sitePluginSchema,
  blocksWidgetSchema,
  panelSchema,
  blocksMainComponentSchema,
  dataComponentSchema,
  webhookSchema,
  ecomAdditionalFeesSchema,
  ecomShippingRatesSchema,
  ecomDiscountsTriggerSchema,
  ecomValidationsSchema,
  ecomPaymentSettingsSchema,
  ecomGiftCardsProviderSchema,
  ecomRecommendationsProviderSchema,
  seoKeywordsSuggestionSchema,
  premiumCustomChargesSchema,
  staffSortingProviderSchema,
  bookingsPricingProviderSchema,
  externalDatabaseProviderSchema,
  topologySchema,
  monitoringSchema,
  siteComponentSchema
]);
var knownCompTypes = new Set(knownComponentSchema.optionsMap.keys());
var unknownComponentPassthroughSchema = z.object({ compType: z.string() }).passthrough();
var componentSchema = z.any().transform((val, ctx) => {
  if (typeof val !== "object" || val == null || typeof val.compType !== "string") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Component must be an object with `compType` property"
    });
    return z.NEVER;
  }
  const schema = knownCompTypes.has(val.compType) ? knownComponentSchema : unknownComponentPassthroughSchema;
  const result = schema.safeParse(val);
  if (!result.success) {
    for (const issue of result.error.issues) {
      ctx.addIssue(issue);
    }
    return z.NEVER;
  }
  return result.data;
});
var appManifestSchema = z.object({
  appId: z.string(),
  components: z.array(componentSchema)
});

// src/read-write.ts
async function readAppManifest(filePath) {
  try {
    const configData = await fs.readFile(filePath, "utf-8");
    return appManifestSchema.parse(JSON.parse(configData));
  } catch (e) {
    const causeMessage = e instanceof Error ? e.toString() : JSON.stringify(e);
    throw new Error(
      `Failed to read App Manifest at ${filePath}: ${causeMessage}`
    );
  }
}
async function writeAppManifest(filePath, appManifest) {
  try {
    await fs.writeFile(filePath, JSON.stringify(appManifest, null, 2));
  } catch (e) {
    const causeMessage = e instanceof Error ? e.toString() : JSON.stringify(e);
    throw new Error(
      `Failed to write App Manifest at ${filePath}: ${causeMessage}`
    );
  }
}

// src/loose.ts
import { ComponentType as ComponentType2 } from "@wix/ambassador-devcenter-apps-v1-app-old/types";
import { z as z2 } from "zod";
var looseBaseComponentSchema = z2.object({
  compData: z2.unknown(),
  compId: z2.string(),
  compName: z2.string().optional(),
  compType: z2.string()
}).passthrough();
var looseWebhookSchema = looseBaseComponentSchema.merge(
  z2.object({
    compData: z2.object({
      webhook: z2.object({
        callbackUrl: z2.string()
      })
    }),
    compType: z2.literal(ComponentType2.WEBHOOK)
  })
);
var looseBackofficeExtensionWidgetSchema = looseBaseComponentSchema.merge(
  z2.object({
    compData: z2.object({
      backOfficeExtensionWidget: z2.object({
        iframeUrl: z2.string()
      })
    }),
    compType: z2.literal(ComponentType2.BACK_OFFICE_EXTENSION_WIDGET)
  })
);
var looseBackofficeExtensionMenuPluginSchema = looseBaseComponentSchema.merge(
  z2.object({
    compData: z2.object({
      backOfficeExtensionMenuItem: z2.object({
        iframeUrl: z2.string()
      })
    }),
    compType: z2.literal(ComponentType2.BACK_OFFICE_EXTENSION_MENU_ITEM)
  })
);
var looseBackofficeModalSchema = looseBaseComponentSchema.merge(
  z2.object({
    compData: z2.object({
      backOfficeModal: z2.object({
        iframeUrl: z2.string()
      })
    }),
    compType: z2.literal(ComponentType2.BACK_OFFICE_MODAL)
  })
);
var looseBackofficePageSchema = looseBaseComponentSchema.merge(
  z2.object({
    compData: z2.object({
      backOfficePage: z2.object({
        iframeUrl: z2.string()
      })
    }),
    compType: z2.literal(ComponentType2.BACK_OFFICE_PAGE)
  })
);
var looseEcomAdditionalFeesSchema = looseBaseComponentSchema.merge(
  z2.object({
    compData: z2.object({
      ecomAdditionalFees: z2.object({
        deploymentUri: z2.string()
      })
    }),
    compType: z2.literal(ComponentType2.ECOM_ADDITIONAL_FEES)
  })
);
var looseEcomDiscountsTriggerSchema = looseBaseComponentSchema.merge(
  z2.object({
    compData: z2.object({
      ecomDiscountsTrigger: z2.object({
        deploymentUri: z2.string()
      })
    }),
    compType: z2.literal(ComponentType2.ECOM_DISCOUNTS_TRIGGER)
  })
);
var looseEcomPaymentSettingsSchema = looseBaseComponentSchema.merge(
  z2.object({
    compData: z2.object({
      ecomPaymentSettings: z2.object({
        deploymentUri: z2.string()
      })
    }),
    compType: z2.literal(ComponentType2.ECOM_PAYMENT_SETTINGS)
  })
);
var looseEcomShippingRatesSchema = looseBaseComponentSchema.merge(
  z2.object({
    compData: z2.object({
      ecomShippingRates: z2.object({
        deploymentUri: z2.string()
      })
    }),
    compType: z2.literal(ComponentType2.ECOM_SHIPPING_RATES)
  })
);
var looseEcomValidationsSchema = looseBaseComponentSchema.merge(
  z2.object({
    compData: z2.object({
      ecomValidations: z2.object({
        deploymentUri: z2.string()
      })
    }),
    compType: z2.literal(ComponentType2.ECOM_VALIDATIONS)
  })
);
var looseEcomGiftCardsProviderSchema = looseBaseComponentSchema.merge(
  z2.object({
    compData: z2.object({
      giftCardsProvider: z2.object({
        deploymentUri: z2.string()
      })
    }),
    compType: z2.literal(ComponentType2.GIFT_CARDS_PROVIDER)
  })
);
var looseStaffSortingProviderSchema = looseBaseComponentSchema.merge(
  z2.object({
    compData: z2.object({
      staffSortingProvider: z2.object({
        deploymentUri: z2.object({
          baseUri: z2.string()
        }),
        methodName: z2.string(),
        methodDescription: z2.string(),
        dashboardPluginId: z2.string().optional()
      })
    }),
    compType: z2.literal(ComponentType2.STAFF_SORTING_PROVIDER)
  })
);
var looseBookingsPricingProviderSchema = looseBaseComponentSchema.merge(
  z2.object({
    compData: z2.object({
      bookingsPricingProvider: z2.object({
        deploymentUri: z2.string(),
        pricingProviderName: z2.string()
      })
    }),
    compType: z2.literal(ComponentType2.BOOKINGS_PRICING_PROVIDER)
  })
);
var looseTopologySchema = looseBaseComponentSchema.merge(
  z2.object({
    compData: z2.object({
      topology: z2.object({
        topology: z2.object({
          backendApiUrl: z2.string().url()
        })
      })
    }),
    compType: z2.literal(ComponentType2.TOPOLOGY)
  })
);
var looseComponentSchema = z2.discriminatedUnion("compType", [
  looseBackofficePageSchema,
  looseBackofficeExtensionWidgetSchema,
  looseBackofficeExtensionMenuPluginSchema,
  looseBackofficeModalSchema,
  looseWebhookSchema,
  looseEcomAdditionalFeesSchema,
  looseStaffSortingProviderSchema,
  looseBookingsPricingProviderSchema,
  looseEcomShippingRatesSchema,
  looseEcomDiscountsTriggerSchema,
  looseEcomValidationsSchema,
  looseEcomPaymentSettingsSchema,
  looseEcomGiftCardsProviderSchema,
  looseTopologySchema
]);
var looseAppManifestSchema = z2.object({
  appId: z2.string(),
  components: z2.array(looseBaseComponentSchema)
});
var looseAppManifestModelSchema = z2.object({
  appId: z2.string(),
  components: z2.array(looseComponentSchema),
  unknownComponents: z2.array(looseBaseComponentSchema)
});

// src/parse.ts
function parseLooseAppManifest(manifest, placeholders = /* @__PURE__ */ new Map()) {
  if (placeholders.size === 0) {
    return looseAppManifestSchema.parse(JSON.parse(manifest));
  }
  let replacedManifest = manifest;
  for (const [key, value] of placeholders.entries()) {
    replacedManifest = replacedManifest.replaceAll(
      key.endsWith("/") ? key.slice(0, -1) : key,
      value.endsWith("/") ? value.slice(0, -1) : value
    );
  }
  return looseAppManifestSchema.parse(JSON.parse(replacedManifest));
}

// src/updateErrorReportingWithArtifact.ts
import rfdc from "rfdc";
var cloneDeep = rfdc();
var updateErrorReportingWithArtifact = (appManifest, artifactData) => {
  const clonedManifest = cloneDeep(appManifest);
  const { artifactId, groupId, version } = artifactData;
  for (const component of clonedManifest.components) {
    for (const componentData of Object.values(component.compData)) {
      const data = componentData;
      if (data.errorReporting) {
        data.errorReporting.artifact = {
          ...data.errorReporting.artifact,
          fullArtifactId: `${groupId}.${artifactId}`,
          version
        };
      }
    }
  }
  return clonedManifest;
};

// src/updateBrowserComponentsWithBaseUrl.ts
import {
  ComponentType as ComponentType3,
  PanelContentType as PanelContentType2
} from "@wix/ambassador-devcenter-apps-v1-app-old/types";
import rfdc2 from "rfdc";

// src/normalizeUrl.ts
var normalizeUrl = (url, normalizedBaseUrl) => {
  return new URL(url.startsWith("/") ? `.${url}` : url, normalizedBaseUrl).href;
};

// src/updateBrowserComponentsWithBaseUrl.ts
var cloneDeep2 = rfdc2();
var overrideComponentSourceWithNormalizedUrls = (componentSource, normalizedBaseUrl) => {
  const { scriptAsset, iframeUrl } = componentSource;
  if (scriptAsset) {
    return {
      scriptAsset: {
        ...scriptAsset,
        url: normalizeUrl(scriptAsset.url, normalizedBaseUrl)
      }
    };
  }
  if (iframeUrl) {
    return {
      iframeUrl: normalizeUrl(iframeUrl, normalizedBaseUrl)
    };
  }
  return componentSource;
};
var overrideComponentTemplateWithNormalizedUrls = (componentSource, normalizedBaseUrl) => {
  return {
    template: componentSource.template.replaceAll(
      "__STATICS_BASE_URL_PLACEHOLDER__",
      normalizedBaseUrl
    )
  };
};
var overrideCustomElementPresetsWithNormalizedAssets = (presets, normalizedBaseUrl) => {
  if (presets) {
    return {
      presets: presets.map((preset) => ({
        ...preset,
        thumbnailUrl: normalizeUrl(preset.thumbnailUrl, normalizedBaseUrl)
      }))
    };
  }
  return {};
};
var overrideSitePluginMarketDataWithNormalizedLogoUrl = (marketData, normalizedBaseUrl) => {
  const logoUrl = marketData.logoUrl;
  if (logoUrl) {
    return {
      logoUrl: normalizeUrl(logoUrl, normalizedBaseUrl)
    };
  }
  return {};
};
var updateBrowserComponentsWithBaseUrl = (appManifest, baseUrl) => {
  const clonedManifest = cloneDeep2(appManifest);
  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  for (const component of clonedManifest.components) {
    const { compData, compType } = component;
    if (compType === ComponentType3.BACK_OFFICE_PAGE) {
      compData.backOfficePage = {
        ...compData.backOfficePage,
        ...overrideComponentSourceWithNormalizedUrls(
          compData.backOfficePage,
          normalizedBaseUrl
        )
      };
    }
    if (compType === ComponentType3.BACK_OFFICE_EXTENSION) {
      compData.backOfficeExtension.widget = {
        ...compData.backOfficeExtension.widget,
        ...overrideComponentSourceWithNormalizedUrls(
          compData.backOfficeExtension.widget,
          normalizedBaseUrl
        )
      };
    }
    if (compType === ComponentType3.BACK_OFFICE_EXTENSION_WIDGET) {
      compData.backOfficeExtensionWidget = {
        ...compData.backOfficeExtensionWidget,
        ...overrideComponentSourceWithNormalizedUrls(
          compData.backOfficeExtensionWidget,
          normalizedBaseUrl
        )
      };
    }
    if (compType === ComponentType3.BACK_OFFICE_MODAL) {
      compData.backOfficeModal = {
        ...compData.backOfficeModal,
        ...overrideComponentSourceWithNormalizedUrls(
          compData.backOfficeModal,
          normalizedBaseUrl
        )
      };
    }
    if (compType === ComponentType3.EMBEDDED_SCRIPT) {
      compData.embeddedScriptComponentData = {
        ...compData.embeddedScriptComponentData,
        ...overrideComponentTemplateWithNormalizedUrls(
          compData.embeddedScriptComponentData,
          normalizedBaseUrl
        )
      };
    }
    if (compType === ComponentType3.CUSTOM_ELEMENT_WIDGET) {
      compData.customElementWidget = {
        ...compData.customElementWidget,
        widgetData: {
          ...compData.customElementWidget.widgetData,
          scriptUrl: normalizeUrl(
            compData.customElementWidget.widgetData.scriptUrl,
            normalizedBaseUrl
          )
        },
        behaviors: {
          ...compData.customElementWidget.behaviors,
          ...compData.customElementWidget.behaviors.settings ? {
            settings: {
              settingsUrl: normalizeUrl(
                compData.customElementWidget.behaviors.settings.settingsUrl,
                normalizedBaseUrl
              )
            }
          } : {}
        },
        ...overrideCustomElementPresetsWithNormalizedAssets(
          compData.customElementWidget.presets,
          normalizedBaseUrl
        )
      };
    }
    if (compType === ComponentType3.WIDGET_PLUGIN) {
      compData.widgetPlugin.marketData = {
        ...compData.widgetPlugin.marketData,
        ...overrideSitePluginMarketDataWithNormalizedLogoUrl(
          compData.widgetPlugin.marketData,
          normalizedBaseUrl
        )
      };
    }
    if (compType === ComponentType3.PANEL && compData.panel.contentType === PanelContentType2.code) {
      compData.panel.code.bundleUrl = normalizeUrl(
        compData.panel.code.bundleUrl,
        normalizedBaseUrl
      );
      if (compData.panel.code.cssUrl) {
        compData.panel.code.cssUrl = normalizeUrl(
          compData.panel.code.cssUrl,
          normalizedBaseUrl
        );
      }
    }
  }
  return clonedManifest;
};

// src/updateBackendComponentsWithBaseUrl.ts
import { ComponentType as ComponentType4 } from "@wix/ambassador-devcenter-apps-v1-app-old/types";
import rfdc3 from "rfdc";
var cloneDeep3 = rfdc3();
var updateBackendComponentsWithBaseUrl = (appManifest, backendServerUrl) => {
  const clonedManifest = cloneDeep3(appManifest);
  for (const component of clonedManifest.components) {
    const { compData, compType } = component;
    if (compType === ComponentType4.WEBHOOK && backendServerUrl) {
      compData.webhook = {
        ...compData.webhook,
        callbackUrl: `${backendServerUrl}/webhooks`
      };
    }
    if (compType === ComponentType4.STAFF_SORTING_PROVIDER && backendServerUrl) {
      compData.staffSortingProvider = {
        ...compData.staffSortingProvider,
        deploymentUri: {
          baseUri: `${backendServerUrl}/service-plugins`
        }
      };
    }
    if (compType === ComponentType4.BOOKINGS_PRICING_PROVIDER && backendServerUrl) {
      compData.bookingsPricingProvider = {
        ...compData.bookingsPricingProvider,
        deploymentUri: `${backendServerUrl}/service-plugins`
      };
    }
    if (compType === ComponentType4.ECOM_ADDITIONAL_FEES && backendServerUrl) {
      compData.ecomAdditionalFees = {
        ...compData.ecomAdditionalFees,
        deploymentUri: `${backendServerUrl}/service-plugins`
      };
    }
    if (compType === ComponentType4.ECOM_SHIPPING_RATES && backendServerUrl) {
      compData.ecomShippingRates = {
        ...compData.ecomShippingRates,
        deploymentUri: `${backendServerUrl}/service-plugins`
      };
    }
    if (compType === ComponentType4.ECOM_VALIDATIONS && backendServerUrl) {
      compData.ecomValidations = {
        ...compData.ecomValidations,
        deploymentUri: `${backendServerUrl}/service-plugins`
      };
    }
    if (compType === ComponentType4.ECOM_DISCOUNTS_TRIGGER && backendServerUrl) {
      compData.ecomDiscountsTrigger = {
        ...compData.ecomDiscountsTrigger,
        deploymentUri: `${backendServerUrl}/service-plugins`
      };
    }
    if (compType === ComponentType4.ECOM_PAYMENT_SETTINGS && backendServerUrl) {
      compData.ecomPaymentSettings = {
        ...compData.ecomPaymentSettings,
        deploymentUri: `${backendServerUrl}/service-plugins`
      };
    }
    if (compType === ComponentType4.GIFT_CARDS_PROVIDER && backendServerUrl) {
      compData.giftCardsProvider = {
        ...compData.giftCardsProvider,
        deploymentUri: `${backendServerUrl}/service-plugins`
      };
    }
    if (compType === ComponentType4.ECOM_RECOMMENDATIONS_PROVIDER && backendServerUrl) {
      compData.ecomRecommendationsProvider = {
        ...compData.ecomRecommendationsProvider,
        deploymentUri: `${backendServerUrl}/service-plugins`
      };
    }
    if (compType === ComponentType4.SEO_KEYWORDS_SUGGESTIONS && backendServerUrl) {
      compData.seoKeywordsSuggestions = {
        ...compData.seoKeywordsSuggestions,
        baseUri: `${backendServerUrl}/service-plugins`
      };
    }
    if (compType === ComponentType4.PREMIUM_CUSTOM_CHARGES && backendServerUrl) {
      compData.premiumCustomCharges = {
        ...compData.premiumCustomCharges,
        baseUri: {
          baseUri: `${backendServerUrl}/service-plugins`
        }
      };
    }
    if (compType === ComponentType4.EXTERNAL_DATABASE_PROVIDER && backendServerUrl) {
      compData.externalDatabaseProvider = {
        ...compData.externalDatabaseProvider,
        uriConfig: {
          baseUri: `${backendServerUrl}/service-plugins`
        }
      };
    }
  }
  return clonedManifest;
};

// src/updateMonitoring.ts
import { ComponentType as ComponentType5 } from "@wix/ambassador-devcenter-apps-v1-app-old/types";
var withMonitoringComponent = (appManifest, updater) => {
  const clonedManifest = structuredClone(appManifest);
  for (const component of clonedManifest.components) {
    if (component.compType === ComponentType5.MONITORING) {
      updater(component);
    }
  }
  return clonedManifest;
};
var updateMonitoring = (appManifest, monitoringData) => {
  const { monitoring, id } = monitoringData;
  if (!id && !monitoring) {
    return appManifest;
  }
  return withMonitoringComponent(appManifest, (component) => {
    if (id) {
      component.compId = id;
    }
    if (monitoring) {
      component.compData.monitoring = monitoring;
    }
  });
};
var hasPanoramaMonitoringFP = (data) => {
  if (typeof data !== "object" || data == null) {
    return false;
  }
  const obj = data;
  if (obj.type !== "PANORAMA") {
    return false;
  }
  const panoramaOptions = obj.panoramaOptions;
  if (typeof panoramaOptions !== "object" || panoramaOptions == null) {
    return false;
  }
  const project = panoramaOptions.project;
  if (typeof project !== "object" || project == null) {
    return false;
  }
  return typeof project.fingerprint === "string";
};
var updatePanoramaFingerprint = (appManifest, fingerprint) => withMonitoringComponent(appManifest, (component) => {
  const monitoringData = component.compData.monitoring;
  if (hasPanoramaMonitoringFP(monitoringData)) {
    monitoringData.panoramaOptions.project.fingerprint = fingerprint;
  }
});

// src/updateTopology.ts
import { ComponentType as ComponentType6 } from "@wix/ambassador-devcenter-apps-v1-app-old/types";
import rfdc4 from "rfdc";
var cloneDeep4 = rfdc4();
var updateTopology = (appManifest, topologyData) => {
  const { baseApiUrl, id } = topologyData;
  if (!id && !baseApiUrl) {
    return appManifest;
  }
  const clonedManifest = cloneDeep4(appManifest);
  for (const component of clonedManifest.components) {
    if (component.compType === ComponentType6.TOPOLOGY) {
      if (id) {
        component.compId = id;
      }
      if (baseApiUrl) {
        component.compData.topology.topology.backendApiUrl = baseApiUrl;
      }
    }
  }
  return clonedManifest;
};

// src/updateBlocksComponents.ts
import { randomUUID } from "node:crypto";
import {
  ComponentType as ComponentType7,
  PanelContentType as PanelContentType3
} from "@wix/ambassador-devcenter-apps-v1-app-old/types";
var updateBlocksComponents = (manifest, blocksComponents) => {
  if (!blocksComponents?.length) {
    return manifest;
  }
  const mainComponentFromBlocks = blocksComponents.find(
    (blocksComponent) => blocksComponent.type === ComponentType7.STUDIO
  );
  const dataComponentFromBlocks = blocksComponents.find(
    (blocksComponent) => blocksComponent.type === ComponentType7.DATA_COMPONENT
  );
  const updatedExtensions = manifest.components.map((comp) => {
    if (comp.compType === ComponentType7.STUDIO) {
      return {
        ...comp,
        compId: mainComponentFromBlocks.id,
        compData: {
          studioComponentData: {
            ...mainComponentFromBlocks.data.studioComponentData
          }
        }
      };
    }
    const componentDataFromBlocks = blocksComponents.find(
      (component) => component.id === comp.compId
    );
    if (!componentDataFromBlocks) {
      return comp;
    }
    if (comp.compType === ComponentType7.STUDIO_WIDGET) {
      return {
        ...comp,
        compName: componentDataFromBlocks.name,
        compData: {
          studioWidgetComponentData: {
            ...componentDataFromBlocks.data.studioWidgetComponentData,
            widgetBundleUrl: comp.compData.studioWidgetComponentData.widgetBundleUrl
          }
        }
      };
    }
    if (comp.compType === ComponentType7.PANEL && comp.compData.panel.contentType === PanelContentType3.url) {
      const codeBundleUrl = new URL(comp.compData.panel.url).searchParams.get(
        "codeBundleUrl"
      );
      const panelUrl = new URL(componentDataFromBlocks.data.panel.url);
      panelUrl.searchParams.set("codeBundleUrl", codeBundleUrl ?? "");
      return {
        ...comp,
        compName: componentDataFromBlocks.name,
        compData: {
          panel: {
            ...componentDataFromBlocks.data.panel,
            url: panelUrl.href,
            contentType: comp.compData.panel.contentType
          }
        }
      };
    }
    return comp;
  });
  if (dataComponentFromBlocks) {
    const dataComponent = {
      compId: dataComponentFromBlocks.id ?? randomUUID(),
      compData: {
        dataComponent: {
          ...dataComponentFromBlocks.data.dataComponent
        }
      },
      compType: ComponentType7.DATA_COMPONENT
    };
    updatedExtensions.push(dataComponent);
  }
  return { ...manifest, components: updatedExtensions };
};

// src/generateUrl.ts
var BASE_URL_PLACEHOLDER = "https://__base_url_placeholder__/";
var generateUrl = (path) => {
  const url = new URL(path, BASE_URL_PLACEHOLDER);
  return url.toString();
};

// src/updateAppManifestComponents.ts
var updateAppManifestComponents = ({
  manifest,
  backendServerUrl,
  baseUrl,
  blocksComponentsData
}) => {
  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  const updatedManifest = updateBlocksComponents(
    JSON.parse(
      JSON.stringify(manifest).replaceAll(
        BASE_URL_PLACEHOLDER,
        normalizedBaseUrl
      )
    ),
    blocksComponentsData
  );
  if (backendServerUrl) {
    return updateBackendComponentsWithBaseUrl(
      updatedManifest,
      backendServerUrl
    );
  }
  return updatedManifest;
};
export {
  BASE_URL_PLACEHOLDER,
  appManifestSchema,
  generateUrl,
  looseAppManifestModelSchema,
  looseAppManifestSchema,
  looseComponentSchema,
  parseLooseAppManifest,
  readAppManifest,
  updateAppManifestComponents,
  updateBackendComponentsWithBaseUrl,
  updateBlocksComponents,
  updateBrowserComponentsWithBaseUrl,
  updateErrorReportingWithArtifact,
  updateMonitoring,
  updatePanoramaFingerprint,
  updateTopology,
  writeAppManifest
};
